import React, { useEffect, useMemo, useReducer, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import queryString from 'query-string'
import { IndexPageActions } from 'src/components/PageLayout/IndexPage/IndexPageActions'
import { StandardOpenModalButton } from 'src/components/ModalForms/ModalButtons'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { ModalFormId } from 'src/components/ModalForms'
import { HUBSPOT } from 'src/Utils/hubspot/hubspot.utils'
import { Tabs, TabType } from 'src/components/Reusable/Tabs'
import { PaginateButtonsGroup } from 'src/components/Reusable/paginate/PaginateButtons'
import { FilterBar } from 'src/components/Reusable/IndexPage/FilterBar'
import { FilterBarConfigs } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.constants'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import { INDEX_PAGE_WITH_FILTERS } from 'src/components/Reusable/IndexPage/IndexPage.constants'
import { CustomFileTypeFilterModal } from 'src/components/Reusable/IndexPage/FilterBar/modals/CustomFileTypeFilterModal'
import {
  ExpandButton,
  StandardButtonSize,
  StandardButtonVariant,
} from 'src/components/Reusable/Buttons'
import { usePaginateItemsWithNavigate } from 'src/components/Reusable/paginate/paginate.hooks'
import {
  Grid,
  GridColumn,
  GridContainer,
  GridHeader,
  GridRow,
} from 'src/components/Reusable/Grid'
import { GridColumnSort } from 'src/components/Reusable/Grid/GridColumnSort/GridColumnSort'
import {
  GridColumnSortActionEvidenceControlMapping,
  GridColumnSortConfigEvidenceControlMapping,
  GridColumnSortEvidenceControlMapping,
  GridColumnSortType,
  GRID_COL_SORT_CONFIGS_EVIDENCE_CONTROL_MAPPING,
} from 'src/components/Reusable/Grid/GridColumnSort/GridColumnSort.constants'
import {
  getColSortIcon,
  getNextColSortType,
  gridColSortReducerEvidenceControlMapping,
} from 'src/components/Reusable/Grid/GridColumnSort/GridColumnSort.utils'
import { NoResults } from 'src/components/PageLayout/NoResults'
import { NoFilterResults } from 'src/components/PageLayout/NoFilterResults'
import { NoControlsIcon } from 'src/components/Icons/EmptyStateIcons'
import { Accordion } from 'react-bootstrap'
import {
  StyledAccordionGridRow,
  StyledAccordionGridRowContent,
} from 'src/components/Reusable/Grid/Grid.styles'
import { EvidenceToControlMappingInfo } from '@trustero/trustero-api-web/lib/evidencemgmt/evidencemgmt_pb'
import { getEvidenceGroupFileTypeIcon } from 'src/components/ModalForms/FileType/useFileTypeIcon'
import { useContent } from 'src/context/hooks'
import { FlexAlign, FlexRow } from 'src/components/Reusable/Flex'
import { Spinner } from 'src/Throbber'
import { TableListLoader } from 'src/components/Reusable/ContentLoaders/TableListLoader'
import {
  IndexPageContainer,
  IndexPageContent,
  IndexPageHeader,
} from 'src/components/PageLayout/PageLayout.styles'
import { useOptionalAppCopilotContext } from 'src/components/Copilot/AppCopilot/AppCopilot.context'
import {
  FileTypeFilterValues,
  FileTypeFilterValueToMimeTypes,
} from 'src/components/Reusable/IndexPage/FilterBar/FilterDropdowns/EvidenceTypeFilterDropdown'
import { useApplySearch } from 'src/Utils/search/search.hooks'
import { DocumentScanTabContent } from '../../Receptors/index/DocumentScanTabContent'
import { EVIDENCE_INDEX_PAGE_SIZE } from '../evidence.constants'
import { FileTypeFilterProvider } from '../context/FileTypeFilterContext'
import {
  useEvidenceFolderScanRefresh,
  usePollEvidenceControlMappings,
} from '../evidence.hooks'
import { EvidenceControlMappings } from '../mappings/EvidenceControlMappings'
import { EvidenceIndexGrid } from './EvidenceIndexGrid'
import {
  useEvidenceGridData,
  useSearchableEvidenceToControlMappingInfo,
} from './EvidenceIndexPage.hooks'
import { sortEvidenceControlMappings } from './EvidenceIndexPage.helper'

export const EvidenceIndexPageActions = ({
  evidenceFolderScanInProgress = false,
}: {
  evidenceFolderScanInProgress?: boolean
}): JSX.Element => {
  const canAddEvidence = useCanPerformAction(CAPABILITY.CAN_ADD_EVIDENCE)
  const scanStatusMessage = evidenceFolderScanInProgress
    ? 'Scanning folder for evidence...'
    : undefined
  return (
    <IndexPageActions
      description="Show you are doing what you say."
      learnMoreUrl={HUBSPOT.GATHERING_EVIDENCE}
      afterLearnMore={
        scanStatusMessage ? (
          <FlexRow align={FlexAlign.CENTER} gap={8} $ml={16}>
            <Spinner size="xs" color="primary" />
            <span>{scanStatusMessage}</span>
          </FlexRow>
        ) : undefined
      }
      buttonGroup={
        <StandardOpenModalButton
          modalId={ModalFormId.ADD_EVIDENCE_GROUPS}
          variant={StandardButtonVariant.PRIMARY}
          size={StandardButtonSize.SMALL}
          text="Add Evidence"
          isDisabled={!canAddEvidence}
          disabledReason="You do not have permission to add evidence."
        />
      }
    />
  )
}

enum EvidenceTabKey {
  EVIDENCE = 'evidence',
  SCANS = 'scans',
  SUGGESTED_CONTROLS = 'suggested-controls',
}

export const NewEvidenceIndexGrid = (): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') || EvidenceTabKey.EVIDENCE

  const tabs: TabType[] = [
    {
      title: 'All Evidence',
      body: <EvidenceTabContent />,
      tabKey: EvidenceTabKey.EVIDENCE,
    },
    {
      title: 'Automated Scan',
      body: <DocumentScanTabContent />,
      tabKey: EvidenceTabKey.SCANS,
    },
    {
      title: 'Suggested Controls',
      body: <EvidenceControlMappingTabContent />,
      tabKey: EvidenceTabKey.SUGGESTED_CONTROLS,
    },
  ]

  const handleTabSelect = (k: string | null) => {
    if (k) {
      searchParams.set('tab', k)
      setSearchParams(searchParams)
    }
  }

  return (
    <Tabs
      tabs={tabs}
      activeKey={activeTab}
      onSelect={(k) => handleTabSelect(k as string | null)}
    />
  )
}

export const EvidenceTabContent = (): JSX.Element => {
  const location = useLocation()
  const appCopilotContext = useOptionalAppCopilotContext()
  const { evidenceFolderScanInProgress } = useEvidenceFolderScanRefresh()
  const {
    evidence,
    hasEvidence,
    isLoading,
    onUpdate,
    paginatedItems,
    matchingEvidence,
    currentPage,
    prevPage,
    nextPage,
    pageCount,
  } = useEvidenceGridData()
  const resultIds = useMemo(
    () => matchingEvidence.map((item) => item.getEvidenceId() || item.getId()),
    [matchingEvidence],
  )
  const resultIdsKey = useMemo(() => resultIds.join(','), [resultIds])
  const hasActiveEvidenceFilters = useMemo(() => {
    const parsed = queryString.parse(location.search, {
      arrayFormat: 'bracket',
    })
    const nonFilterParams = new Set([
      'tab',
      FilterParam.PAGE,
      'pc',
      'contentSearch',
      FilterParam.SORT_COLUMN,
      FilterParam.SORT_ORDER,
    ])
    return Object.keys(parsed).some((key) => !nonFilterParams.has(key))
  }, [location.search])
  const hasFilteredResults =
    hasActiveEvidenceFilters || matchingEvidence.length !== evidence.length
  const contextLabel = hasFilteredResults
    ? `Filtered evidence including ${matchingEvidence.length} results`
    : 'All evidence'
  const nextMetadataFilterQuery = location.search
  const nextMetadataResultIds = useMemo(
    () => (hasFilteredResults ? resultIds : []),
    [hasFilteredResults, resultIds],
  )
  const nextMetadataResultsCount = matchingEvidence.length
  const nextMetadataResultIdsKey = hasFilteredResults ? resultIdsKey : ''

  useEffect(() => {
    if (!appCopilotContext) {
      return
    }
    appCopilotContext.setCurrentContextLabel(contextLabel)
    appCopilotContext.setReferringContextMetadata({
      filterQuery: nextMetadataFilterQuery,
      resultIds: nextMetadataResultIds,
      resultsCount: nextMetadataResultsCount,
    })
  }, [
    appCopilotContext,
    contextLabel,
    nextMetadataFilterQuery,
    nextMetadataResultIds,
    nextMetadataResultIdsKey,
    nextMetadataResultsCount,
  ])

  return (
    <IndexPageContainer>
      <IndexPageHeader>
        <EvidenceIndexPageActions
          evidenceFolderScanInProgress={evidenceFolderScanInProgress}
        />
        <FileTypeFilterProvider>
          <>
            <FilterBar
              config={FilterBarConfigs[INDEX_PAGE_WITH_FILTERS.EVIDENCE]}
              searchBarOptions={{ withContentSearch: true }}
            />
            <CustomFileTypeFilterModal />
          </>
        </FileTypeFilterProvider>
      </IndexPageHeader>
      <IndexPageContent>
        <GridContainer>
          <EvidenceIndexGrid
            evidence={paginatedItems}
            hasEvidence={hasEvidence}
            isLoading={isLoading}
            onUpdate={onUpdate}
          />
        </GridContainer>
        <PaginateButtonsGroup
          totalItems={matchingEvidence.length}
          pageSize={pageCount > 0 ? pageCount : EVIDENCE_INDEX_PAGE_SIZE}
          currentPage={currentPage}
          onPrevClick={prevPage}
          onNextClick={nextPage}
        />
      </IndexPageContent>
    </IndexPageContainer>
  )
}

const EVIDENCE_CONTROL_MAPPING_GRID_TEMPLATE =
  'min-content repeat(3, 1fr) min-content'

const EvidenceControlMappingColumnSort = ({
  column,
  columnMapState,
  onSort,
}: {
  column: GridColumnSortEvidenceControlMapping
  columnMapState: GridColumnSortConfigEvidenceControlMapping
  onSort: (action: GridColumnSortActionEvidenceControlMapping) => void
}): JSX.Element => {
  const state = columnMapState[column]
  return (
    <GridColumnSort
      colName={column}
      showSpinner={false}
      currIcon={getColSortIcon(state.type, state.isActive)}
      applySorting={(e) => {
        e.preventDefault()
        onSort({ colName: column, type: getNextColSortType(state.type) })
      }}
    />
  )
}

const EvidenceControlMappingGridHeader = ({
  columnMapState,
  onSort,
}: {
  columnMapState: GridColumnSortConfigEvidenceControlMapping
  onSort: (action: GridColumnSortActionEvidenceControlMapping) => void
}): JSX.Element => {
  return (
    <GridRow>
      <GridHeader>Type</GridHeader>
      <GridHeader>
        Evidence
        <EvidenceControlMappingColumnSort
          column={GridColumnSortEvidenceControlMapping.EVIDENCE}
          columnMapState={columnMapState}
          onSort={onSort}
        />
      </GridHeader>
      <GridHeader>
        Linked Controls
        <EvidenceControlMappingColumnSort
          column={GridColumnSortEvidenceControlMapping.LINKED_CONTROLS}
          columnMapState={columnMapState}
          onSort={onSort}
        />
      </GridHeader>
      <GridHeader>
        Suggested Controls
        <EvidenceControlMappingColumnSort
          column={GridColumnSortEvidenceControlMapping.SUGGESTED_CONTROLS}
          columnMapState={columnMapState}
          onSort={onSort}
        />
      </GridHeader>
      <GridHeader></GridHeader>
    </GridRow>
  )
}

export const EvidenceControlMappingGridRow = ({
  mapping,
}: {
  mapping: EvidenceToControlMappingInfo
}): JSX.Element => {
  const content = useContent()
  const [isExpanded, setIsExpanded] = useState(false)

  const handleRowClick = () => {
    setIsExpanded((prev) => !prev)
  }

  const FileTypeIcon = getEvidenceGroupFileTypeIcon({
    isAutomated: mapping.getEvidence()?.getIsAutomated() ?? false,
    isLinkedDocument: mapping.getEvidence()?.getIsLinkedDocument() ?? false,
    mime: mapping.getEvidence()?.getMime() ?? '',
    serviceModelId: mapping.getEvidence()?.getServiceModelId() ?? '',
    content: content,
  })

  return (
    <>
      <StyledAccordionGridRow
        as={Accordion}
        isExpanded={isExpanded}
        onClick={handleRowClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleRowClick()
          }
        }}
      >
        <GridColumn>
          <FileTypeIcon width={20} height={20} />
        </GridColumn>
        <GridColumn>{mapping.getEvidence()?.getCaption()}</GridColumn>
        <GridColumn>{mapping.getLinkedControlsList().length}</GridColumn>
        <GridColumn>{mapping.getSuggestedControlsList().length}</GridColumn>
        <GridColumn onClick={(e) => e.stopPropagation()}>
          <ExpandButton
            onToggled={() => setIsExpanded((prev) => !prev)}
            isExpanded={isExpanded}
          />
        </GridColumn>
      </StyledAccordionGridRow>
      {isExpanded && (
        <StyledAccordionGridRowContent>
          <EvidenceControlMappings mapping={mapping} />
        </StyledAccordionGridRowContent>
      )}
    </>
  )
}

const EvidenceControlMappingGrid = ({
  mappings,
  columnMapState,
  onSort,
}: {
  mappings: EvidenceToControlMappingInfo[]
  columnMapState: GridColumnSortConfigEvidenceControlMapping
  onSort: (action: GridColumnSortActionEvidenceControlMapping) => void
}): JSX.Element => {
  return (
    <Grid gridTemplateColumns={EVIDENCE_CONTROL_MAPPING_GRID_TEMPLATE}>
      <EvidenceControlMappingGridHeader
        columnMapState={columnMapState}
        onSort={onSort}
      />
      {mappings.map((mapping) => (
        <EvidenceControlMappingGridRow
          mapping={mapping}
          key={mapping.getEvidence()?.getId()}
        />
      ))}
    </Grid>
  )
}

export const ReceptorSpinner = (): JSX.Element => {
  return (
    <FlexRow gap={24} justify={FlexAlign.FLEX_START} isFullWidth $mb={8}>
      <Spinner color="primary" size="m"></Spinner>
      <p>Collecting evidence from receptor...</p>
    </FlexRow>
  )
}

export const IndexingEvidenceSpinner = (): JSX.Element => {
  return (
    <FlexRow gap={24} justify={FlexAlign.FLEX_START} isFullWidth $mb={8}>
      <Spinner color="primary" size="m"></Spinner>
      <p>Analyzing evidence to find suggested controls...</p>
    </FlexRow>
  )
}

export const EvidenceControlMappingTabGrid = ({
  evidenceIds,
  isIndexPage = false,
  isScanning = false,
  mappings: externalMappings,
  isPolling: externalIsPolling,
  isLoading: externalIsLoading,
}: {
  evidenceIds?: string[]
  isIndexPage?: boolean
  isScanning?: boolean
  mappings?: EvidenceToControlMappingInfo[]
  isPolling?: boolean
  isLoading?: boolean
}): JSX.Element => {
  const [searchParameter] = useSearchParams()
  const pageCount = Number(searchParameter.get('pc'))
  const {
    mappings: polledMappings,
    isPolling: polledIsPolling,
    isLoading: polledIsLoading,
  } = usePollEvidenceControlMappings({
    evidenceIds,
    defaultToAll: isIndexPage,
  })
  const mappings = externalMappings ?? polledMappings
  const isPolling = externalIsPolling ?? polledIsPolling
  const isLoading = externalIsLoading ?? polledIsLoading

  const [columnMapState, dispatch] = useReducer(
    gridColSortReducerEvidenceControlMapping,
    GRID_COL_SORT_CONFIGS_EVIDENCE_CONTROL_MAPPING(
      GridColumnSortType.ASCENDING,
      GridColumnSortEvidenceControlMapping.EVIDENCE,
    ),
  )

  const handleSort = (action: GridColumnSortActionEvidenceControlMapping) => {
    dispatch(action)
  }

  const activeEntry = (
    Object.entries(columnMapState) as [
      GridColumnSortEvidenceControlMapping,
      { type: GridColumnSortType; isActive: boolean },
    ][]
  ).find(([, val]) => val.isActive)
  const sortCol =
    activeEntry?.[0] ?? GridColumnSortEvidenceControlMapping.EVIDENCE
  const sortType = activeEntry?.[1].type ?? GridColumnSortType.ASCENDING

  const searchableMappings = useSearchableEvidenceToControlMappingInfo(mappings)
  const { data: searchMatchingMappings } = useApplySearch(searchableMappings)

  const matchingMappings = useMemo(() => {
    let filtered = searchMatchingMappings

    // Client-side filtering for File Type
    const fileTypes = searchParameter.getAll(FilterParam.FILE_TYPE)
    if (fileTypes?.length) {
      const allowedMimes = fileTypes.flatMap(
        (ft) =>
          (FileTypeFilterValueToMimeTypes[
            ft as FileTypeFilterValues
          ] as string[]) || [ft],
      )
      filtered = filtered.filter((m) => {
        const mime = m.getEvidence()?.getMime() || ''
        return allowedMimes.includes(mime)
      })
    }

    // Client-side filtering for Control
    const controlIds = searchParameter.getAll(FilterParam.CONTROL)
    if (controlIds?.length) {
      filtered = filtered.filter((m) => {
        const linkedControls = m.getLinkedControlsList().map((c) => c.getId())
        const suggestedControls = m
          .getSuggestedControlsList()
          .map((c) => c.getId())
        return controlIds.some(
          (id) => linkedControls.includes(id) || suggestedControls.includes(id),
        )
      })
    }

    return filtered
  }, [searchMatchingMappings, searchParameter])

  const sortedMappings = useMemo(
    () =>
      sortEvidenceControlMappings(matchingMappings || [], sortCol, sortType),
    [matchingMappings, sortCol, sortType],
  )

  const { paginatedItems, currentPage, prevPage, nextPage } =
    usePaginateItemsWithNavigate<EvidenceToControlMappingInfo>({
      items: sortedMappings,
      pageSize: pageCount > 0 ? pageCount : EVIDENCE_INDEX_PAGE_SIZE,
    })

  return isLoading ? (
    <GridContainer>
      <TableListLoader />
    </GridContainer>
  ) : mappings.length > 0 ? (
    <>
      <GridContainer>
        {isScanning && <ReceptorSpinner />}
        {isPolling && <IndexingEvidenceSpinner />}
        {matchingMappings.length > 0 ? (
          <EvidenceControlMappingGrid
            mappings={isIndexPage ? paginatedItems : sortedMappings}
            columnMapState={columnMapState}
            onSort={handleSort}
          />
        ) : (
          <NoFilterResults />
        )}
      </GridContainer>
      {isIndexPage && (
        <PaginateButtonsGroup
          totalItems={sortedMappings.length}
          pageSize={pageCount > 0 ? pageCount : EVIDENCE_INDEX_PAGE_SIZE}
          currentPage={currentPage}
          onPrevClick={prevPage}
          onNextClick={nextPage}
        />
      )}
    </>
  ) : isPolling || isScanning ? (
    <GridContainer>
      {isScanning && <ReceptorSpinner />}
      {isPolling && <IndexingEvidenceSpinner />}
      <TableListLoader />
    </GridContainer>
  ) : (
    <GridContainer>
      <NoResults
        icons={<NoControlsIcon />}
        title="No Suggested Controls"
        note="No suggested controls found for this evidence."
      />
    </GridContainer>
  )
}

export const EvidenceControlMappingTabContent = ({
  evidenceIds,
}: {
  evidenceIds?: string[]
}): JSX.Element => {
  return (
    <IndexPageContainer>
      <IndexPageHeader>
        <FileTypeFilterProvider>
          <>
            <FilterBar
              config={FilterBarConfigs[INDEX_PAGE_WITH_FILTERS.EVIDENCE]}
              searchBarOptions={{ withContentSearch: true }}
            />
            <CustomFileTypeFilterModal />
          </>
        </FileTypeFilterProvider>
      </IndexPageHeader>
      <IndexPageContent>
        <EvidenceControlMappingTabGrid evidenceIds={evidenceIds} isIndexPage />
      </IndexPageContent>
    </IndexPageContainer>
  )
}
